import { AssetTypeCode } from 'types/models/asset-type';
import { DAVIS_SOIL_MOISTURE_SENSOR_TAG_IDS, SensorTagId } from 'types/models/samplePoint';
import { WeatherAssetType, WeatherUnitType } from 'types/weather.enum';
import { UnitType } from 'utils/get-unit-by-country';

/**
 * Soil Moisture units are determined by sensor hardware, not asset type alone:
 * Davis sensors use `cb`, while Spectrum sensors use `VSW%`.
 * Excluding Soil Moisture from this map forces callers to provide `sensorTagId` when `AssetTypeCode.SOIL` is used.
 */
export type NonSoilMoistureWeatherAssetType = Exclude<WeatherAssetType, AssetTypeCode.SOIL>;

const WEATHER_ASSET_TYPE_TO_UNIT_TYPE: Record<NonSoilMoistureWeatherAssetType, WeatherUnitType> = {
  [AssetTypeCode.BAROMETRIC_PRESSURE]: UnitType.PRESSURE_MD,
  [AssetTypeCode.DELTA_T]: UnitType.TEMPERATURE,
  [AssetTypeCode.DEW_POINT]: UnitType.TEMPERATURE,
  [AssetTypeCode.EVAPOTRANSPIRATION]: UnitType.RAIN,
  [AssetTypeCode.HUMIDITY]: UnitType.HUMIDITY,
  [AssetTypeCode.RAIN_GAUGE]: UnitType.RAIN,
  [AssetTypeCode.SOIL_TEMPERATURE]: UnitType.TEMPERATURE,
  [AssetTypeCode.SOLAR_RADIATION]: UnitType.SOLAR_RADIATION,
  [AssetTypeCode.TEMPERATURE]: UnitType.TEMPERATURE,
  [AssetTypeCode.WEATHER_STATION_BATTERY]: UnitType.VOLTAGE,
  [AssetTypeCode.WIND]: UnitType.WIND_SPEED
};

type WeatherAssetTypeToUnitTypeParams =
  | { assetType: NonSoilMoistureWeatherAssetType }
  | { assetType: AssetTypeCode.SOIL; sensorTagId: SensorTagId; };

/**
 * Resolves the `WeatherUnitType` used for display and unit conversion.
 * Soil Moisture or `AssetTypeCode.SOIL` requires `sensorTagId`.
 * So Davis sensors resolve to `cb` and Spectrum sensors resolve to to `VSW%`.
 *
 * @see getUnitTypeByAssetTypeId for the equivalent non-weather asset type mapping.
 */
export function weatherAssetTypeToUnitType(params: WeatherAssetTypeToUnitTypeParams): WeatherUnitType {
  if (params.assetType === AssetTypeCode.SOIL) {
    const isDavisSoilMoisture = DAVIS_SOIL_MOISTURE_SENSOR_TAG_IDS.includes(params.sensorTagId);
    if (isDavisSoilMoisture) {
      return UnitType.SOIL_MOISTURE_CB;
    }
    return UnitType.SOIL_MOISTURE_VSW;
  }

  return WEATHER_ASSET_TYPE_TO_UNIT_TYPE[params.assetType];
}